import { useState } from 'react';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ApiError } from '../../../../lib/apiError';
import { usersApi } from '../usersApi';
import type { UserListItem } from '../userTypes';

/**
 * Bulk deactivation from the selection bar.
 *
 * Runs sequentially and reports partial outcomes rather than failing the whole set: the
 * server refuses to deactivate the last active administrator, so one refusal inside a
 * selection is expected and must not hide the successes.
 */
export function BulkDeactivateDialog({
  users,
  onClose,
  onDone,
}: {
  users: UserListItem[] | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [running, setRunning] = useState(false);
  const [failures, setFailures] = useState<{ email: string; reason: string }[]>([]);

  if (!users || users.length === 0) {
    return null;
  }

  async function run() {
    if (!users) return;

    setRunning(true);
    setFailures([]);

    const failed: { email: string; reason: string }[] = [];

    for (const user of users) {
      try {
        await usersApi.deactivate(user.id);
      } catch (caught) {
        failed.push({
          email: user.email,
          reason: caught instanceof ApiError ? caught.message : 'Unknown error.',
        });
      }
    }

    setRunning(false);

    if (failed.length === 0) {
      onDone();
      return;
    }

    // Keep the dialog open so the refusals are actually read, not flashed past.
    setFailures(failed);
  }

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      onConfirm={() => (failures.length > 0 ? onDone() : void run())}
      title="Deactivate users"
      /*
        THE ONE OVERRIDE IN THE PRODUCT, and it is here because this dialog STOPS BEING ONE: once
        the run has reported what it could not deactivate, the button dismisses a report rather
        than confirming anything, and `Confirm` on it would be asking a question already answered.
      */
      confirmLabel={failures.length > 0 ? 'Close' : undefined}
      danger={failures.length === 0}
      requireConfirm={failures.length === 0}
      requireCredential={failures.length === 0}
      loading={running}
      id="users-bulk-deactivate-dialog"
      body={
        failures.length > 0 ? (
          <div className="flex flex-col gap-2">
            <p>
              {users.length - failures.length} of {users.length} deactivated.{' '}
              {failures.length} could not be:
            </p>
            <ul className="flex flex-col gap-1">
              {failures.map((f) => (
                <li key={f.email} className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs">
                  <span className="font-mono tabular-nums">{f.email}</span> — {f.reason}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p>
            {users.length} account{users.length === 1 ? '' : 's'} will be signed out everywhere and
            will no longer be able to sign in. The records are kept and can be restored.
          </p>
        )
      }
    />
  );
}
